import React from 'react';
import { useSelector } from 'react-redux';
import { Section, SectionTitle } from '@repo/components/containers';
import { attributesForContactInfo } from '@repo/utils/models/Contact';
import { ContactInfo, ID } from '@repo/utils/types';
import styles from './Sources.module.scss';
import analytics from '@repo/utils/helpers/analytics';
import { useAuthContext, useThemedIconForSource } from '@repo/hooks';
import SourceActionsMenu from './ActionsMenu';
import PhoneSource from './PhoneSource';
import CopySourceBtn from './CopySourceBtn';
import { ContactSource, selectContactSources } from '@repo/redux/reducers/data/sources';
import { selectContactInteractionsSummary } from '@repo/redux/reducers/data/moments';

function SourceIcon({ source }: { source: ContactSource }) {
  const icon = useThemedIconForSource(source, { baseIconOnIntegration: true });
  return <span className={styles.icon} style={{ backgroundImage: `url(${icon})` }} />;
}

function handleLink() {
  analytics.track('Contact Contacted');
}

function Source({
  source,
  contactID,
  isShared,
  noActions,
}: {
  source: ContactSource;
  contactID: ID;
  isShared?: boolean;
  noActions?: boolean;
}) {
  const { getIsFreePlan } = useAuthContext();
  const { href, target, displayValue, label } = attributesForContactInfo(source, getIsFreePlan());

  if (href && !noActions) {
    if (source.type === 'email') {
      return (
        <div className={styles.source}>
          <a className={styles.value} href={href} target={target} onClick={handleLink}>
            {displayValue}
          </a>
          <div className={styles.label}>{label}</div>
          <SourceActionsMenu
            contactID={contactID}
            source={source}
            triggerClassName={styles.sourceActionsMenuTrigger}
            isShared={isShared}
          />
        </div>
      );
    }

    if (source.type === 'phone') {
      return (
        <div className={styles.source}>
          <PhoneSource source={source} />
          <div className={styles.label}>{label}</div>
          <SourceActionsMenu
            contactID={contactID}
            source={source}
            triggerClassName={styles.sourceActionsMenuTrigger}
            isShared={isShared}
          />
        </div>
      );
    }

    return (
      <a className={styles.source} href={href} target={target} onClick={handleLink}>
        <span className={styles.value}>{displayValue}</span>
        <CopySourceBtn value={href} className={styles.copyBtn} />
      </a>
    );
  }

  return (
    <div className={styles.source}>
      {source.value}
      {!noActions && <CopySourceBtn value={source.value} className={styles.copyBtn} />}
    </div>
  );
}

export const SourcesList = ({
  contactSources,
  contactID,
  isShared,
  noActions,
}: {
  contactSources: ContactSource[];
  contactID?: ID;
  isShared?: boolean;
  noActions?: boolean;
}) => {
  if (contactSources.length === 0) {
    return null;
  }

  return (
    <>
      {contactSources.map((source) => (
        <div className={styles.sourceContainer} key={`${source.source}_${source.value}`}>
          <SourceIcon source={source} />
          <Source contactID={contactID} source={source} isShared={isShared} noActions={noActions} />
        </div>
      ))}
    </>
  );
};

export const SourcesSection = ({
  contactID,
  summary,
  sources,
  isShared,
}: {
  contactID?: ID;
  summary?: String[];
  sources?: ContactInfo[];
  isShared?: boolean;
}) => {
  let contactSources = useSelector(selectContactSources(contactID)).items.valueSeq().toArray();
  contactSources = sources ? (sources as ContactSource[]) : contactSources;

  if (contactSources.length === 0) {
    return null;
  }

  return (
    <Section>
      <SectionTitle underline>Sources</SectionTitle>
      {summary && <p>{summary.join(' ')}</p>}
      {contactSources.map((source) => (
        <div className={styles.sourceContainer} key={source.id}>
          <SourceIcon source={source} />
          <Source contactID={contactID} source={source} isShared={isShared} />
        </div>
      ))}
    </Section>
  );
};

export default function Sources({
  sources,
  contactID,
  isSelf = false,
}: {
  sources?: ContactInfo[];
  contactID?: ID;
  isSelf?: boolean;
}) {
  const summary = useSelector(selectContactInteractionsSummary(contactID || ''));
  return <SourcesSection contactID={contactID} sources={sources} summary={!isSelf && summary} />;
}
